import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, ChevronDown, Bell, Megaphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { categories } from '../mock/mockData';
import { searchAPI, adsAPI } from '../services/api';
import api from '../services/api';
import { getImageUrl, formatPrice } from '../utils/helpers';

const FALLBACK_PHRASES = [
  "Buscar tenis nuevos...",
  "Buscar perfumes...",
  "Buscar celulares...",
  "Buscar laptops gaming...",
  "Buscar productos, marcas y mas...",
];

const STATIC_PLACEHOLDER = "Buscar en Zona Libre...";

const useTypewriter = (phrases, active, typingSpeed = 80, pauseTime = 2000) => {
  const [text, setText] = useState('');
  const phraseIdx = useRef(0);
  const charIdx = useRef(0);
  const isDeleting = useRef(false);

  useEffect(() => {
    if (!active || !phrases || phrases.length === 0) {
      setText('');
      return;
    }
    phraseIdx.current = 0;
    charIdx.current = 0;
    isDeleting.current = false;

    const tick = () => {
      const current = phrases[phraseIdx.current];
      if (!current) return typingSpeed;
      if (!isDeleting.current) {
        charIdx.current++;
        setText(current.substring(0, charIdx.current));
        if (charIdx.current === current.length) {
          isDeleting.current = true;
          return pauseTime;
        }
        return typingSpeed;
      } else {
        charIdx.current--;
        setText(current.substring(0, charIdx.current));
        if (charIdx.current === 0) {
          isDeleting.current = false;
          phraseIdx.current = (phraseIdx.current + 1) % phrases.length;
          return 400;
        }
        return typingSpeed / 2;
      }
    };

    let timer;
    const schedule = () => {
      const delay = tick();
      timer = setTimeout(schedule, delay);
    };
    timer = setTimeout(schedule, typingSpeed);
    return () => clearTimeout(timer);
  }, [phrases, active, typingSpeed, pauseTime]);

  return text;
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const { getCartCount } = useCart();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [suggestions, setSuggestions] = useState({ organic: [], sponsored: null });
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Dynamic typewriter config
  const [twActive, setTwActive] = useState(true);
  const [twPhrases, setTwPhrases] = useState(FALLBACK_PHRASES);

  useEffect(() => {
    api.get('/search/config')
      .then(res => {
        setTwActive(res.data.is_typewriter_active);
        const phrases = res.data.typewriter_phrases;
        if (Array.isArray(phrases) && phrases.length > 0) setTwPhrases(phrases);
      })
      .catch(() => {});
  }, []);

  const placeholderText = useTypewriter(twPhrases, twActive);

  // Hide navbar on scroll down, show on scroll up
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > 80 && y > lastScrollY.current);
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions({ organic: [], sponsored: null });
      setShowDropdown(false);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const data = await searchAPI.autocomplete(query.trim());
      setSuggestions(data);
      setShowDropdown(true);
    } catch {
      setSuggestions({ organic: [], sponsored: null });
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowDropdown(false);
    if (searchQuery.trim()) {
      const term = searchQuery.trim();
      navigate(`/search?q=${encodeURIComponent(term)}`);
    }
  };

  const handleSuggestionClick = (item) => {
    setShowDropdown(false);
    setSearchQuery('');
    if (item.type === 'sponsored' && item.campaign_id) {
      adsAPI.recordClick(item.campaign_id).catch(() => {});
    }
    navigate(`/product/${item.id}`);
  };

  const sellLink = user ? '/seller/products' : '/vender';

  return (
    <nav
      className={`bg-zl-yellow sticky top-0 z-50 shadow-md transition-transform duration-300 ${hidden ? '-translate-y-full' : 'translate-y-0'}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-3 md:px-4">
        {/* Top bar */}
        <div className="flex items-center justify-between py-2 md:py-3 gap-2 md:gap-0">
          {/* Logo */}
          <Link to="/" className="shrink-0" data-testid="navbar-logo">
            <img
              src="/logo-zonalibre.png"
              alt="Zona Libre"
              className="h-9 md:h-10 w-auto"
            />
          </Link>

          {/* Search bar with autocomplete */}
          <form onSubmit={handleSearch} className="flex-1 min-w-0 mx-2 md:mx-8 md:max-w-2xl" ref={dropdownRef}>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                placeholder={twActive ? (placeholderText || ' ') : STATIC_PLACEHOLDER}
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={() => { if (suggestions.organic.length || suggestions.sponsored) setShowDropdown(true); }}
                className="w-full px-4 py-2.5 pr-12 rounded-sm border-0 shadow-sm focus:outline-none focus:ring-2 focus:ring-zl-orange"
                data-testid="search-input"
              />
              <button
                type="submit"
                className="absolute right-0 top-0 h-full px-4 bg-zl-orange hover:bg-zl-orange-600 transition-colors"
                data-testid="search-submit-btn"
              >
                <Search className="w-5 h-5 text-white" />
              </button>

              {/* Autocomplete Dropdown */}
              {showDropdown && (suggestions.organic.length > 0 || suggestions.sponsored) && (
                <div
                  className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 z-[60] overflow-hidden"
                  data-testid="autocomplete-dropdown"
                >
                  {suggestions.sponsored && (
                    <div
                      onClick={() => handleSuggestionClick(suggestions.sponsored)}
                      className="flex items-center gap-3 px-4 py-3 bg-amber-50 hover:bg-amber-100 cursor-pointer border-b border-amber-200 transition-colors"
                      data-testid="autocomplete-sponsored-item"
                    >
                      <img
                        src={getImageUrl(suggestions.sponsored.image)}
                        alt=""
                        className="w-10 h-10 object-cover rounded"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/40'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{suggestions.sponsored.title}</p>
                        <p className="text-sm font-semibold text-zl-orange">{formatPrice(suggestions.sponsored.price)}</p>
                      </div>
                      <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-200 px-2 py-0.5 rounded-full shrink-0">
                        <Megaphone className="w-3 h-3" />
                        Patrocinado
                      </span>
                    </div>
                  )}

                  {suggestions.organic.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSuggestionClick(item)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      data-testid="autocomplete-organic-item"
                    >
                      <Search className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-700 truncate flex-1">{item.title}</span>
                      <span className="text-xs text-gray-400 shrink-0">{item.category}</span>
                    </div>
                  ))}

                  {searchQuery.trim() && (
                    <div
                      onClick={handleSearch}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-zl-orange-50 cursor-pointer text-zl-orange font-medium text-sm"
                      data-testid="autocomplete-search-all"
                    >
                      <Search className="w-4 h-4" />
                      Buscar "{searchQuery}" en toda la tienda
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>

          {/* Right section */}
          <div className="flex items-center space-x-2 md:space-x-6 shrink-0">
            {user ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center space-x-0 md:space-x-2 hover:bg-zl-yellow-400 text-zl-brown px-2 md:px-3">
                      <User className="w-5 h-5" />
                      <div className="text-left hidden md:block">
                        <div className="text-xs text-zl-brown-400">Hola, {user.name?.split(' ')[0]}</div>
                        <div className="text-sm font-semibold flex items-center">
                          Mi cuenta <ChevronDown className="w-3 h-3 ml-1" />
                        </div>
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="navbar-go-panel">
                      Ir a mi panel
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="navbar-logout">
                      Cerrar sesion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Notifications */}
                <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative hover:bg-zl-yellow-400 text-zl-brown">
                      <Bell className="w-5 h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-zl-orange text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                          {unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-96 max-h-96 overflow-y-auto">
                    <div className="flex items-center justify-between p-3 border-b">
                      <h3 className="font-semibold text-zl-brown">Notificaciones</h3>
                      {unreadCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={markAllAsRead}
                          className="text-xs text-zl-orange hover:text-zl-orange-600"
                        >
                          Marcar todas como leidas
                        </Button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No tienes notificaciones</p>
                      </div>
                    ) : (
                      <div>
                        {notifications.slice(0, 10).map(notification => (
                          <div
                            key={notification._id}
                            className={`p-3 border-b hover:bg-gray-50 cursor-pointer ${
                              !notification.read ? 'bg-zl-yellow-50' : ''
                            }`}
                            onClick={() => {
                              markAsRead(notification._id);
                              if (notification.link) navigate(notification.link);
                            }}
                          >
                            <p className="font-medium text-sm text-zl-brown">{notification.title}</p>
                            <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(notification.created_at || notification.timestamp).toLocaleDateString('es-MX')}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="hover:bg-zl-yellow-400 text-zl-brown font-semibold px-2 md:px-3"
              >
                <User className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">Ingresa</span>
              </Button>
            )}

            <Link to="/cart" className="relative hover:opacity-80 transition-opacity text-zl-brown">
              <ShoppingCart className="w-6 h-6" />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-zl-orange text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                  {getCartCount()}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Categories bar */}
        <div className="border-t border-zl-yellow-600 py-2 overflow-x-auto scrollbar-hide">
          <div className="flex items-center space-x-4 md:space-x-6 text-sm text-zl-brown whitespace-nowrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="hover:bg-zl-yellow-400 px-3 py-1 text-zl-brown">
                  <Menu className="w-4 h-4 mr-2" />
                  Categorias
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {categories.map(cat => (
                  <DropdownMenuItem
                    key={cat.id}
                    onClick={() => navigate(`/category/${cat.name}`)}
                  >
                    {cat.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/search?sort=offers" className="hover:opacity-70 transition-opacity font-medium">
              Ofertas
            </Link>
            <Link to="/historial" className="hover:opacity-70 transition-opacity">
              Historial
            </Link>
            <Link to="/category/Moda" className="hover:opacity-70 transition-opacity">
              Moda
            </Link>
            <Link to={sellLink} className="hover:opacity-70 transition-opacity font-medium" data-testid="navbar-sell-link">
              Vender
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
